import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  EMAIL_LOG_PAGE_SIZE,
  type EmailDeliveryStatus,
  type EmailEventDto,
  type EmailLogEntryDto,
} from "@tc/shared";
import { Card, Icon, IconButton, Modal, Pagination, Tag, Timeline, type IconName, type TagVariant } from "@/components/ui";
import { useClubFilter } from "@/lib/clubs/club-filter";
import { useFormatters } from "@/lib/i18n/format";
import { emailHtmlUrl, listEmailEvents, listEmailLog } from "./api";

/**
 * Back-office email log (TRAV-59): every email the platform has sent, newest first, scoped by
 * the topbar club filter like the other ops surfaces. Each row links to the stored HTML body in
 * a new tab — the exact message the recipient received, not a re-render.
 */
export function EmailLogPage() {
  const { t } = useTranslation();
  const { scopeId } = useClubFilter();

  const [entries, setEntries] = useState<EmailLogEntryDto[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(false);

  // A club-filter change resets to the first page of the new scope.
  useEffect(() => {
    setPage(1);
  }, [scopeId]);

  const load = useCallback(async () => {
    setError(false);
    try {
      const result = await listEmailLog(page, scopeId);
      setEntries(result.items);
      setTotal(result.total);
    } catch {
      setError(true);
    }
  }, [page, scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(total / EMAIL_LOG_PAGE_SIZE));

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t("emailLog.title")}</h1>
        <div className="sub">{t("emailLog.sub")}</div>
      </div>

      {error ? (
        <div className="empty">{t("emailLog.loadError")}</div>
      ) : entries === null ? (
        <div className="empty">{t("common.loading")}</div>
      ) : entries.length === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="mail" size={22} />
          </div>
          <div style={{ fontWeight: 600 }}>{t("emailLog.empty")}</div>
        </div>
      ) : (
        <>
          <Card>
            <table className="table">
              <thead>
                <tr>
                  <th>{t("emailLog.sentAt")}</th>
                  <th>{t("emailLog.club")}</th>
                  <th>{t("emailLog.recipient")}</th>
                  <th>{t("emailLog.type")}</th>
                  <th>{t("emailLog.subject")}</th>
                  <th>{t("emailLog.status")}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <EmailRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </Card>
          <Pagination
            page={page}
            pageCount={pageCount}
            onPage={setPage}
            prevLabel={t("trips.pager.prev")}
            nextLabel={t("trips.pager.next")}
            summary={t("trips.pager.summary", {
              from: (page - 1) * EMAIL_LOG_PAGE_SIZE + 1,
              to: Math.min(page * EMAIL_LOG_PAGE_SIZE, total),
              total,
            })}
          />
        </>
      )}
    </div>
  );
}

/**
 * Delivery-state badge palette (TRAV-61). Bounce/complaint render danger — AWS penalises
 * unwatched bounce rates, so failures must jump out of the table.
 */
const STATUS_VARIANT: Record<EmailDeliveryStatus, TagVariant> = {
  sent: "neutral",
  delivered: "success",
  opened: "primary",
  clicked: "primary",
  bounced: "danger",
  complained: "danger",
};

/** SES event type → timeline node icon; anything unrecognised falls back to `mail`. */
const EVENT_ICON: Record<string, IconName> = {
  Send: "send",
  Delivery: "check",
  Open: "eye",
  Click: "arrowR",
  Bounce: "alert",
  Complaint: "alert",
};

function EmailRow({ entry }: { entry: EmailLogEntryDto }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [eventsOpen, setEventsOpen] = useState(false);

  return (
    <tr>
      <td style={{ whiteSpace: "nowrap" }}>
        {fmt.dateTime(entry.createdAt, { dateStyle: "medium", timeStyle: "short" })}
      </td>
      <td>{entry.clubShortCode ?? <span className="faint">—</span>}</td>
      <td className="t-trip">
        <b>{entry.recipientName}</b>
        <small>{entry.recipientEmail}</small>
      </td>
      <td>
        <span className="tag mono">{entry.template}</span>
      </td>
      <td>{entry.subject}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        {entry.deliveryStatus ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Tag variant={STATUS_VARIANT[entry.deliveryStatus]} dot>
              {t(`emailLog.deliveryStatus.${entry.deliveryStatus}`)}
            </Tag>
            <IconButton
              name="clock"
              size={14}
              label={t("emailLog.events.open")}
              onClick={() => setEventsOpen(true)}
            />
            <EmailEventsModal entry={entry} open={eventsOpen} onClose={() => setEventsOpen(false)} />
          </span>
        ) : (
          <span className="faint">—</span>
        )}
      </td>
      <td>
        <a
          className="link-row"
          href={emailHtmlUrl(entry.id)}
          target="_blank"
          rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}
        >
          {t("emailLog.view")} <Icon name="arrowR" size={14} />
        </a>
      </td>
    </tr>
  );
}

/**
 * SES event history for one message (TRAV-61): what the provider reported, in event order —
 * the detail behind the folded status badge. Fetched lazily when the modal opens.
 */
function EmailEventsModal({ entry, open, onClose }: { entry: EmailLogEntryDto; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [events, setEvents] = useState<EmailEventDto[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(false);
    listEmailEvents(entry.id)
      .then(setEvents)
      .catch(() => setError(true));
  }, [open, entry.id]);

  return (
    <Modal open={open} onClose={onClose} title={t("emailLog.events.title")}>
      <div className="modal-body">
        <div className="sub" style={{ marginBottom: 12 }}>
          {entry.subject} — {entry.recipientEmail}
        </div>
        {error ? (
          <div className="empty">{t("emailLog.loadError")}</div>
        ) : events === null ? (
          <div className="empty">{t("common.loading")}</div>
        ) : events.length === 0 ? (
          <div className="empty">{t("emailLog.events.empty")}</div>
        ) : (
          <Timeline
            variant="audit"
            items={events.map((event) => ({
              key: event.id,
              node: <Icon name={EVENT_ICON[event.eventType] ?? "mail"} size={13} />,
              title: t(`emailLog.eventType.${event.eventType}`, { defaultValue: event.eventType }),
              meta: fmt.dateTime(event.occurredAt, { dateStyle: "medium", timeStyle: "medium" }),
            }))}
          />
        )}
      </div>
    </Modal>
  );
}
